import { useEffect } from "react"
import type { EmblaCarouselType } from "embla-carousel"

/**
 * Horizontal wheel travel (px) that advances the carousel by one snap.
 * When a user scrolls horizontally with a trackpad, the accumulated
 * delta must reach this threshold to trigger a snap advance.
 */
const WHEEL_STEP_PX = 40

interface UseHorizontalWheelScrollOptions {
  /** Whether to enable the hook (default: true) */
  enabled?: boolean
}

/**
 * Hook to enable horizontal trackpad/wheel scrolling on an Embla carousel.
 *
 * Embla only handles pointer drags, so horizontal trackpad/wheel gestures do
 * nothing on their own. This hook translates horizontal wheel deltas into snap steps,
 * accumulating until a step's worth of travel has been scrolled so a single
 * flick doesn't skip several slides.
 *
 * @param embla - The initialized Embla carousel instance
 * @param options - Optional configuration
 *
 * The instance must be held in state (not a ref) so that the component
 * re-renders once Embla initializes and this hook's effect can attach.
 *
 * @example
 * ```tsx
 * const [embla, setEmbla] = useState<EmblaCarouselType | null>(null)
 * useHorizontalWheelScroll(embla)
 * return <Carousel onInit={setEmbla}>...</Carousel>
 * ```
 */
const useHorizontalWheelScroll = (
  embla: EmblaCarouselType | null,
  options: UseHorizontalWheelScrollOptions = {}
) => {
  const { enabled = true } = options

  useEffect(() => {
    if (!embla || !enabled) return

    const root = embla.rootNode()
    let accumulatedDelta = 0

    const handleWheel = (event: WheelEvent) => {
      // Only handle horizontal scrolling; let vertical scroll through
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return
      event.preventDefault()

      accumulatedDelta += event.deltaX
      if (Math.abs(accumulatedDelta) < WHEEL_STEP_PX) return

      const direction = accumulatedDelta > 0 ? 1 : -1
      accumulatedDelta = 0
      embla.scrollTo(embla.selectedScrollSnap() + direction)
    }

    root.addEventListener("wheel", handleWheel, { passive: false })
    embla.on("destroy", () => root.removeEventListener("wheel", handleWheel))

    return () => {
      root.removeEventListener("wheel", handleWheel)
    }
  }, [embla, enabled])
}

export default useHorizontalWheelScroll
